import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { OpportunityTable } from "@/components/sales/OpportunityTable";
import { QuoteRows } from "@/components/sales/QuoteRows";
import { NewQuoteDialog } from "@/components/sales/NewQuoteDialog";

export const useCompanySales = (companyId) => {
  const { can } = useAuth();
  const [d, setD] = useState({ opps: [], quotes: [] });
  const load = useCallback(() => {
    if (can("opportunities:read")) api.get("/sales/opportunities", { params: { company_id: companyId, limit: 50 } }).then((r) => setD((x) => ({ ...x, opps: r.data.items }))).catch(() => {});
    if (can("quotes:read")) api.get("/sales/quotes", { params: { company_id: companyId, limit: 50 } }).then((r) => setD((x) => ({ ...x, quotes: r.data.items }))).catch(() => {});
  }, [companyId, can]);
  useEffect(() => { load(); }, [load]);
  return { ...d, reload: load };
};

export const CompanyPipelineSummary = ({ opps, quotes }) => {
  const open = opps.filter((o) => o.status === "open");
  const live = quotes.filter((q) => ["bozza", "inviata"].includes(q.status));
  return (
    <Panel className="p-5" data-testid="company-sales-summary"><SectionLabel>Commerciale</SectionLabel>
      {open.length === 0 && live.length === 0 ? <p className="mt-2 text-sm text-slate-500">Nessuna opportunità aperta.</p> : (
        <ul className="mt-2 divide-y divide-slate-100">
          {open.map((o) => <li key={o.id}><Link to={`/opportunita/${o.id}`} className="block py-2.5 hover:bg-slate-50" data-testid={`company-opp-${o.id}`}>
            <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-semibold text-ink">{o.title}</p><StatusBadge tone={o.stage?.tone}>{o.stage?.name}</StatusBadge></div>
            <p className="text-xs text-slate-500">{fmtCurrency(o.value)} · {o.probability}% · {o.next_action ? <span className={o.followup_overdue ? "font-semibold text-red-600" : ""}>{fmtDate(o.next_action_at)} {o.next_action.title}</span> : <span className="text-amber-700">nessuna prossima azione</span>}</p>
          </Link></li>)}
          {live.map((q) => <li key={q.id}><Link to={`/offerte/${q.id}`} className="flex items-center justify-between py-2 text-xs text-slate-600 hover:bg-slate-50">
            <span><span className="font-mono">{q.number}</span> · {q.title}</span><span className="font-semibold text-ink">{fmtCurrency(q.taxable)}<span className="ml-1 text-[11px] font-normal text-slate-400">+ IVA</span></span></Link></li>)}
        </ul>)}
    </Panel>
  );
};

export const CompanyOpportunitiesTab = ({ opps, onNew }) => {
  const { can } = useAuth();
  return (
    <Panel className="overflow-x-auto" data-testid="company-opportunities-tab">
      <div className="flex items-center justify-between p-4"><SectionLabel>Opportunità</SectionLabel>{can("opportunities:create") && <Button size="sm" onClick={onNew} data-testid="company-tab-new-opp">Nuova opportunità</Button>}</div>
      {opps.length === 0 ? <p className="px-4 pb-6 text-sm text-slate-500">Nessuna opportunità per questo cliente.</p> : <OpportunityTable items={opps} f={{}} hideCompany />}
    </Panel>
  );
};

export const CompanyQuotesTab = ({ quotes, companyId }) => {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <Panel className="p-5" data-testid="company-quotes-tab">
      <div className="mb-2 flex items-center justify-between"><SectionLabel>Offerte</SectionLabel>{can("quotes:create") && <Button size="sm" onClick={() => setOpen(true)} data-testid="company-tab-new-quote">Nuova offerta</Button>}</div>
      <QuoteRows quotes={quotes} empty="Nessuna offerta per questo cliente." />
      <NewQuoteDialog open={open} onOpenChange={setOpen} companyId={companyId} />
    </Panel>
  );
};
